import { useCallback, useEffect, useMemo, useState } from 'react';
import type { UseAriaKeyboardNavigationOptions, UseAriaKeyboardNavigationReturn } from './types';

/**
 * Hook for accessible ARIA keyboard navigation patterns
 *
 * Implements WCAG 2.1 keyboard navigation patterns for ARIA widgets:
 * - 2.1.1 Keyboard - All functionality available via keyboard
 * - 2.4.3 Focus Order - Logical navigation order
 * - 4.1.2 Name, Role, Value - Proper ARIA roles and states
 *
 * @example
 * // Horizontal tabs
 * const { containerProps, getItemProps } = useAriaKeyboardNavigation({
 *   pattern: 'tabs',
 *   direction: 'horizontal',
 *   itemsRef,
 *   totalItems: 3,
 *   selectedIndex,
 *   onSelect: setSelectedIndex,
 * });
 *
 * @example
 * // Vertical listbox (single select)
 * const { containerProps, getItemProps } = useAriaKeyboardNavigation({
 *   pattern: 'listbox',
 *   direction: 'vertical',
 *   itemsRef,
 *   totalItems: options.length,
 *   selectedIndex,
 *   onSelect: handleSelect,
 * });
 */
export function useAriaKeyboardNavigation(options: UseAriaKeyboardNavigationOptions): UseAriaKeyboardNavigationReturn {
  const {
    pattern,
    direction = pattern === 'tabs' ? 'horizontal' : 'vertical',
    itemsRef,
    totalItems,
    selectedIndex,
    onSelect,
    focusStrategy = 'roving-tabindex',
    loop = true,
    autoActivate = false,
    disabled = false,
    idPrefix = 'nav-item',
  } = options;

  // Track focused index separately from selected index
  // This is important for tabs with manual activation (autoActivate=false)
  // where focus moves with arrows but selection only happens on Enter/Space
  const [focusedIndex, setFocusedIndex] = useState(selectedIndex);

  // Sync focusedIndex with selectedIndex when selectedIndex changes externally
  // (e.g., when user confirms selection or parent component changes selection)
  useEffect(() => {
    setFocusedIndex(selectedIndex);
  }, [selectedIndex]);

  // Get the next index based on direction
  const getNextIndex = useCallback(
    (currentIndex: number, delta: number): number => {
      // Guard against empty lists - return current index to prevent invalid navigation
      if (totalItems === 0) {
        return currentIndex;
      }

      const nextIndex = currentIndex + delta;

      if (loop) {
        // Wrap around
        if (nextIndex < 0) {
          return totalItems - 1;
        }
        if (nextIndex >= totalItems) {
          return 0;
        }
        return nextIndex;
      } else {
        // Clamp to bounds
        return Math.max(0, Math.min(nextIndex, totalItems - 1));
      }
    },
    [totalItems, loop],
  );

  // Move focus to item at index
  const moveFocus = useCallback(
    (index: number) => {
      const item = itemsRef.current?.[index];
      if (item) {
        item.focus();
      }
    },
    [itemsRef],
  );

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (disabled || totalItems === 0) {
        return;
      }

      let handled = false;
      let newIndex = focusedIndex; // Use focusedIndex for navigation

      // Determine if key should be handled based on direction
      const isHorizontalKey = e.key === 'ArrowLeft' || e.key === 'ArrowRight';
      const isVerticalKey = e.key === 'ArrowUp' || e.key === 'ArrowDown';
      const isArrowKey = isHorizontalKey || isVerticalKey;

      // Check if this key applies to our direction
      const shouldHandleKey =
        direction === 'both' ||
        (direction === 'horizontal' && isHorizontalKey) ||
        (direction === 'vertical' && isVerticalKey);

      if (shouldHandleKey && isArrowKey) {
        // Navigation keys
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
          newIndex = getNextIndex(focusedIndex, 1);
          handled = true;
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
          newIndex = getNextIndex(focusedIndex, -1);
          handled = true;
        }

        if (handled) {
          e.preventDefault();
          e.stopPropagation();
          setFocusedIndex(newIndex); // Update focused index
          moveFocus(newIndex);

          // Only activate if autoActivate is true
          // When false: arrows only move focus, user must press Enter/Space to activate
          // When true: arrows move focus AND activate immediately
          if (autoActivate) {
            onSelect?.(newIndex);
          }
        }
      }

      // Home/End keys (for all patterns)
      if (e.key === 'Home') {
        e.preventDefault();
        e.stopPropagation();
        newIndex = 0;
        setFocusedIndex(newIndex);
        moveFocus(newIndex);
        if (autoActivate) {
          onSelect?.(newIndex);
        }
        handled = true;
      } else if (e.key === 'End') {
        e.preventDefault();
        e.stopPropagation();
        newIndex = totalItems - 1;
        setFocusedIndex(newIndex);
        moveFocus(newIndex);
        if (autoActivate) {
          onSelect?.(newIndex);
        }
        handled = true;
      }

      // Enter/Space for activation
      // All patterns support Enter/Space when autoActivate is false
      if (e.key === 'Enter' || e.code === 'Space') {
        e.preventDefault();
        e.stopPropagation();
        onSelect?.(focusedIndex); // Use focusedIndex for activation
        handled = true;
      }

      // Escape for menu pattern (close menu - parent should handle)
      if (e.key === 'Escape' && pattern === 'menu') {
        e.preventDefault();
        e.stopPropagation();
        // Parent component should listen for this and close the menu
      }
    },
    [
      disabled,
      direction,
      focusedIndex, // Changed from selectedIndex
      pattern,
      autoActivate,
      getNextIndex,
      moveFocus,
      onSelect,
      totalItems,
    ],
  );

  // Get container role based on pattern
  const containerRole = useMemo(() => {
    switch (pattern) {
      case 'tabs':
        return 'tablist';
      case 'listbox':
        return 'listbox';
      case 'menu':
        return 'menu';
      case 'radiogroup':
        return 'radiogroup';
      default:
        return 'list';
    }
  }, [pattern]);

  // Get item role based on pattern
  const getItemRole = useMemo(() => {
    switch (pattern) {
      case 'tabs':
        return 'tab';
      case 'listbox':
        return 'option';
      case 'menu':
        return 'menuitem';
      case 'radiogroup':
        return 'radio';
      default:
        return 'listitem';
    }
  }, [pattern]);

  // Container props
  const containerProps = useMemo(
    () => ({
      role: containerRole,
      ...(focusStrategy === 'aria-activedescendant' && {
        'aria-activedescendant': `${idPrefix}-${focusedIndex}`,
      }),
      onKeyDown: handleKeyDown,
    }),
    [containerRole, focusStrategy, idPrefix, focusedIndex, handleKeyDown],
  );

  // Function to get item props
  const getItemProps = useCallback(
    (index: number) => {
      const isSelected = index === selectedIndex;
      const isFocused = index === focusedIndex;
      const itemId = `${idPrefix}-${index}`;

      // Determine tabIndex based on activation mode
      let itemTabIndex = -1;
      if (focusStrategy === 'roving-tabindex') {
        if (autoActivate) {
          // Auto-activation: focused item gets tabindex=0 (focus and selection move together)
          itemTabIndex = isFocused ? 0 : -1;
        } else {
          // Manual activation: selected item always has tabindex=0
          // If no item is selected (selectedIndex < 0), first item gets tabindex=0 (ARIA best practice)
          // This ensures Tab key focuses the selected item, while arrows move focus programmatically
          const shouldBeInTabSequence = isSelected || (selectedIndex < 0 && index === 0);
          itemTabIndex = shouldBeInTabSequence ? 0 : -1;
        }
      }

      return {
        role: getItemRole,
        id: itemId,
        ...(pattern === 'tabs' || pattern === 'listbox' ? { 'aria-selected': isSelected } : {}),
        ...(pattern === 'radiogroup' ? { 'aria-checked': isSelected } : {}),
        ...(pattern === 'tabs' ? { 'aria-controls': `${idPrefix}-panel-${index}` } : {}),
        tabIndex: itemTabIndex,
        ref: (el: HTMLElement | null) => {
          if (itemsRef.current) {
            itemsRef.current[index] = el;
          }
        },
      };
    },
    [selectedIndex, focusedIndex, idPrefix, getItemRole, pattern, focusStrategy, itemsRef, autoActivate],
  );

  return {
    containerProps,
    getItemProps,
  };
}

export type {
  NavigationPattern,
  NavigationDirection,
  FocusStrategy,
  UseAriaKeyboardNavigationOptions,
  UseAriaKeyboardNavigationReturn,
} from './types';
